import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/ui/brand-mark";
import { Section } from "../components/ui/section";
import { useAuth } from "../auth/AuthContext";

const SUPPORT_EMAIL = "support@tailspend.kinesis.studio";

/**
 * Public support / contact page. Mirrors AboutPage and PrivacyPage's
 * self-contained shell (its own header + max-w-2xl content column) so it
 * renders without the authenticated AppLayout — it's reachable while signed
 * out, which is what the App Store / Play listing links to for a support URL.
 */
export function SupportPage(): ReactNode {
  const { status } = useAuth();
  const home = status === "authenticated" ? "/" : "/welcome";

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <header className="border-b border-outline-variant/40 bg-surface-container-lowest">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to={home} className="flex items-center gap-3">
            <BrandMark />
            <span className="text-headline-md text-primary">Tailspend</span>
          </Link>
          {status === "authenticated" ? (
            <Link to="/">
              <Button variant="ghost" size="sm">
                Back to dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/welcome">
              <Button size="sm">Sign in</Button>
            </Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-stack-lg px-6 py-10 sm:px-8">
        <div className="space-y-2">
          <p className="text-label-sm text-on-surface-variant">Support</p>
          <h1 className="text-headline-lg">Help &amp; support</h1>
          <p className="text-body-md text-on-surface-variant">
            Need a hand with Tailspend? We&apos;re happy to help with anything
            from getting started to questions about your groups, expenses, or
            account.
          </p>
        </div>

        <Section
          title="Contact us"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            Email us at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-primary hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            and we&apos;ll get back to you. We typically reply within two
            business days.
          </p>
          <p>
            To help us help you faster, let us know which device you&apos;re on
            (iOS, Android, or web), the version of the app, and a short
            description of what happened.
          </p>
        </Section>

        <Section
          title="Common questions"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            <strong className="text-on-surface">
              Adding people to a group.
            </strong>{" "}
            Open the group, share its invite link, and approve people as they
            ask to join. You can also share a read-only view with anyone who
            just needs to follow along.
          </p>
          <p>
            <strong className="text-on-surface">Splitting an expense.</strong>{" "}
            When you record an expense you can split the whole amount or
            individual items — equally, by exact amounts, by percentages, or by
            shares.
          </p>
          <p>
            <strong className="text-on-surface">Managing your account.</strong>{" "}
            Update your display name and preferred currency from your profile.
            To request access to or deletion of your data, just email us.
          </p>
        </Section>

        <Section
          title="More information"
          contentClassName="space-y-3 text-body-md text-on-surface-variant"
        >
          <p>
            Learn more about what Tailspend does on the{" "}
            <Link to="/about" className="text-primary hover:underline">
              About
            </Link>{" "}
            page, or read how we handle your data in our{" "}
            <Link to="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </Section>
      </main>
    </div>
  );
}
